import { ChevronLeftIcon, SearchIcon } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { cn } from '../../shared/lib/cn';
import { fold } from '../../shared/lib/fold';
import { Logo } from '../../shared/ui/logo';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../shared/ui/tooltip';
import type { NavItem, ShellSlot } from './nav-config';

/** A sidebar destination: 40px, 8px radius, translucent fills on navy (`sidebar-item-*`). */
const itemClass =
  'group flex h-10 items-center gap-3 rounded-sm px-3 text-sm text-sidebar-foreground no-underline transition-colors duration-150 ease-standard hover:bg-sidebar-item-hover hover:text-sidebar-foreground aria-[current=page]:bg-sidebar-item-active aria-[current=page]:font-bold';

/** A quieter sidebar action ("Back to projects", "New workspace"): 36px, muted until hovered. */
export const sidebarActionClass =
  'flex h-9 w-full cursor-pointer items-center gap-3 rounded-sm px-3 text-left text-sm text-sidebar-muted-foreground no-underline transition-colors duration-150 ease-standard hover:bg-sidebar-item-hover hover:text-sidebar-foreground';

export function RailTooltip({
  show,
  label,
  children,
}: {
  show: boolean;
  label: string;
  children: ReactNode;
}) {
  if (!show) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

function ItemLink({
  item,
  active,
  collapsed,
  strong,
  onNavigate,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  /** Bottom-group items are bold whether active or not. */
  strong?: boolean;
  onNavigate?: () => void;
}) {
  return (
    <RailTooltip show={collapsed} label={item.label}>
      <Link
        to={item.to}
        onClick={onNavigate}
        {...(active ? { 'aria-current': 'page' as const } : {})}
        {...(collapsed ? { 'aria-label': item.label } : {})}
        className={cn(itemClass, strong && 'font-bold', collapsed && 'w-10 justify-center px-0')}
      >
        <item.icon
          aria-hidden="true"
          className={cn(
            'size-4.5 shrink-0',
            !strong && !active && 'text-sidebar-muted-foreground group-hover:text-current',
          )}
        />
        {collapsed ? null : <span className="truncate">{item.label}</span>}
      </Link>
    </RailTooltip>
  );
}

type SidebarContentProps = {
  items: NavItem[];
  /** Heading of the primary navigation (the workspaces of the active org, or the settings sections). */
  navLabel: string;
  /** When set, a search field above the list filters it by name. Hidden on the rail. */
  searchLabel?: string;
  /** Above the heading: the way out of settings mode. */
  backLink?: { label: string; to: string };
  /** Rendered under the list: "New workspace", the archived toggle. */
  navFooterSlot?: ShellSlot;
  /** At the foot of the sidebar, after a separator: Settings, Staff area, the design reference. */
  bottomItems?: NavItem[];
  /** The organization switcher, last of all. */
  switcherSlot?: ShellSlot;
  collapsed: boolean;
  /** Set inside the mobile drawer: called after a link is followed, so the drawer closes. */
  onNavigate?: () => void;
  /** False for a picture of the sidebar (the design reference): plain containers, no navigation landmarks. */
  landmarks?: boolean;
};

/** Everything inside the navy sidebar; shared by the desktop aside and the mobile drawer. */
export function SidebarContent({
  items,
  navLabel,
  searchLabel,
  backLink,
  navFooterSlot,
  bottomItems = [],
  switcherSlot,
  collapsed,
  onNavigate,
  landmarks = true,
}: SidebarContentProps) {
  const Nav = landmarks ? 'nav' : 'div';
  const slotContext = {
    collapsed,
    inDrawer: onNavigate !== undefined,
    ...(onNavigate ? { onNavigate } : {}),
  };
  const { t } = useTranslation('shell');
  const { pathname } = useLocation();
  const [query, setQuery] = useState('');
  // The filter is a way of getting somewhere; once there it gets out of the way.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on every navigation
  useEffect(() => setQuery(''), [pathname]);
  const searching = searchLabel !== undefined && !collapsed;
  const needle = searching ? fold(query.trim()) : '';
  const shown = needle ? items.filter((item) => fold(item.label).includes(needle)) : items;

  return (
    <div
      className={cn(
        'flex h-full flex-col gap-4 py-4 [&_:focus-visible]:outline-sidebar-ring',
        collapsed ? 'items-center px-2' : 'px-3',
      )}
    >
      <Link
        to="/"
        aria-label={t('home')}
        onClick={onNavigate}
        className={cn('block rounded-sm', collapsed ? 'px-0 py-2' : 'px-3 pt-2 pb-4')}
      >
        <Logo tone="inverse" decorative className={collapsed ? 'w-10' : 'w-20'} />
      </Link>

      <Nav
        {...(landmarks ? { 'aria-label': navLabel } : {})}
        className="flex w-full min-h-0 flex-col gap-1"
      >
        {backLink ? (
          <RailTooltip show={collapsed} label={backLink.label}>
            <Link
              to={backLink.to}
              onClick={onNavigate}
              {...(collapsed ? { 'aria-label': backLink.label } : {})}
              className={cn(sidebarActionClass, collapsed && 'w-10 justify-center px-0')}
            >
              <ChevronLeftIcon aria-hidden="true" className="size-5 shrink-0" />
              {collapsed ? null : <span className="truncate">{backLink.label}</span>}
            </Link>
          </RailTooltip>
        ) : null}
        {collapsed ? null : (
          <p
            className={cn(
              'px-3 pb-1 text-xs font-bold text-sidebar-muted-foreground',
              backLink && 'mt-2',
            )}
          >
            {navLabel}
          </p>
        )}
        {searching ? (
          <div className="flex h-9 items-center gap-2 rounded-sm bg-sidebar-field px-3 focus-within:outline-2 focus-within:outline-sidebar-ring">
            <SearchIcon
              aria-hidden="true"
              className="size-4 shrink-0 text-sidebar-muted-foreground"
            />
            {/* design-system-allow: borderless search on navy, styled with the sidebar-field token */}
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-label={searchLabel}
              placeholder={searchLabel}
              className="min-w-0 flex-1 bg-transparent text-sm text-sidebar-foreground outline-none placeholder:text-sidebar-muted-foreground [&::-webkit-search-cancel-button]:hidden"
            />
          </div>
        ) : null}
        {shown.length === 0 && needle ? (
          <p className="px-3 py-2 text-sm text-sidebar-muted-foreground">{t('nav.noMatches')}</p>
        ) : (
          <ul className="grid max-h-90 gap-1 overflow-y-auto">
            {shown.map((item) => (
              <li key={item.key}>
                <ItemLink
                  item={item}
                  active={item.isActive(pathname)}
                  collapsed={collapsed}
                  {...(onNavigate ? { onNavigate } : {})}
                />
              </li>
            ))}
          </ul>
        )}
        {navFooterSlot ? <div className="grid gap-1">{navFooterSlot(slotContext)}</div> : null}
      </Nav>

      <div className="mt-auto flex w-full min-w-0 flex-col gap-1">
        {bottomItems.length > 0 ? (
          <Nav {...(landmarks ? { 'aria-label': t('nav.more') } : {})}>
            <div aria-hidden="true" className="mx-1 mb-2 h-px bg-sidebar-border" />
            <ul className="grid gap-1">
              {bottomItems.map((item) => (
                <li key={item.key}>
                  <ItemLink
                    item={item}
                    active={item.isActive(pathname)}
                    collapsed={collapsed}
                    strong
                    {...(onNavigate ? { onNavigate } : {})}
                  />
                </li>
              ))}
            </ul>
          </Nav>
        ) : null}
        {switcherSlot ? (
          // biome-ignore lint/a11y/useSemanticElements: a labelled group, not a form fieldset
          <div role="group" aria-label={t('switcher')} className="mt-2 w-full">
            {switcherSlot(slotContext)}
          </div>
        ) : null}
      </div>
    </div>
  );
}
